import { ObservationSource } from "src/constants/ObservationSource";
import {
  ObservationBundle,
  ObservationBundleCategory
} from "src/types/observationLaboratory";

const PROVIDER_IDENTIFIER_SYSTEM = "9am-backend/lab-provider";
const SOURCE_IDENTIFIER_SYSTEM = "9am-backend/observation-source";
const LAB_ORDER_IDENTIFIER_SYSTEM = "9am-backend";
const LAB_ORDER_CODE = "lab-order-id";
const OBSERVATION_DATE_CODE = "observation-date";

export const OBSERVATION_BUNDLE_HIE_SOURCES = [
  "connectivehealth.io",
  "fake-connectivehealth.io"
];

// _→- so internal-questionnaire ↔ INTERNAL_QUESTIONNAIRE, care-team-upload ↔ CARE_TEAM_UPLOAD
/** Normalizes a performer/source value for case-, separator-, and trailing-slash-insensitive matching. */
export const normalizeValue = (value?: string): string =>
  (value ?? "").trim().toLowerCase().replace(/_/g, "-").replace(/\/+$/, "");

// URL → hostname (strip scheme, path, query, leading www.); non-URL → normalized token
const toHost = (value?: string): string => {
  const n = normalizeValue(value);
  if (!n.includes("://")) {
    return n;
  }
  const host = n.split("://")[1]?.split(/[/?]/)[0] ?? "";
  return host.replace(/^www\./, "");
};

/** True if value equals candidate (token) or its host matches the candidate domain. */
export const valueMatches = (value?: string, candidate?: string): boolean => {
  const nv = normalizeValue(value);
  const nc = normalizeValue(candidate);
  if (!nv || !nc) {
    return false;
  }
  if (nv === nc) {
    return true;
  }
  const hv = toHost(value);
  return hv === nc || hv.endsWith("." + nc);
};

const matchesAny = (value: string | undefined, candidates: string[]): boolean =>
  candidates.some((c) => valueMatches(value, c));

export const PROVIDER_CATEGORY_MAP: Array<{
  category: ObservationBundleCategory;
  values: string[];
}> = [
  {
    category: ObservationBundleCategory.AT_HOME,
    values: ["GETLABS", "getlabs.com"]
  },
  {
    category: ObservationBundleCategory.SELF_TESTING,
    values: [ObservationSource.TASSO, "tassoinc.com"]
  },
  {
    category: ObservationBundleCategory.REGULAR,
    values: [
      "QUEST",
      ObservationSource.QUEST_DIAGNOSTICS,
      "questdiagnostics.com",
      ObservationSource.LABCORP,
      "labcorp.com",
      ObservationSource.EVERLYWELL,
      "everlywell.com",
      "bioreference.com",
      "truepill.com",
      "other-trusted-lab"
    ]
  }
];

export const SOURCE_CATEGORY_MAP = PROVIDER_CATEGORY_MAP;

/** DiagnosticReport entries only — the resource type that carries performer identifiers. */
const getDiagnosticReportPerformerIdentifiers = (
  bundle: ObservationBundle,
  system: string
): string[] =>
  (bundle.entry ?? [])
    .filter((entry) => entry.resource?.resourceType === "DiagnosticReport")
    .flatMap((entry) => entry.resource?.performer ?? [])
    .filter((performer) => performer.identifier?.system === system)
    .map((performer) => performer.identifier?.value)
    .filter((value): value is string => Boolean(value));

/**
 * Recognizable lab source/provider value → human display name. Used as a fallback
 * for imported/HIE bundles that carry no performer.display (only lab-ordered bundles
 * get one from the backend, via LabOrderProviderType.getDisplayName). Wording matches
 * that backend display where they overlap (Labcorp/Getlabs/Tasso) and the FEWEB-2424
 * design ("Quest Diagnostics"). Aggregator-only sources (connectivehealth.io) are
 * intentionally absent → they resolve to undefined ("Not provided").
 */
export const LAB_DISPLAY_NAME_ENTRIES: Array<{
  display: string;
  values: string[];
}> = [
  { display: "Labcorp", values: [ObservationSource.LABCORP, "labcorp.com"] },
  {
    display: "Quest Diagnostics",
    values: [
      "QUEST",
      ObservationSource.QUEST_DIAGNOSTICS,
      "questdiagnostics.com"
    ]
  },
  { display: "Getlabs", values: ["GETLABS", "getlabs.com"] },
  { display: "Tasso", values: [ObservationSource.TASSO, "tassoinc.com"] },
  {
    display: "Everlywell",
    values: [ObservationSource.EVERLYWELL, "everlywell.com"]
  },
  {
    display: "BioReference",
    values: [
      ObservationSource.BIOREFERENCE,
      ObservationSource.BIOREFERENCE_QA,
      "bioreference.com"
    ]
  },
  {
    display: "Truepill",
    values: [
      ObservationSource.TRUEPILL,
      ObservationSource.TRUEPILL_DIAGNOSTICS,
      "truepill.com"
    ]
  }
];

const labDisplayNameFor = (values: string[]): string | undefined =>
  values
    .map(
      (v) =>
        LAB_DISPLAY_NAME_ENTRIES.find((entry) =>
          entry.values.some((c) => valueMatches(v, c))
        )?.display
    )
    .find(Boolean);

/**
 * Human-readable source/facility name for a bundle. Prefers the lab-order provider's
 * performer.display (ordered labs); for imported/HIE bundles with no display, falls
 * back to mapping a recognizable source/provider value to a name. Undefined when
 * nothing is recognizable (e.g. aggregator-only HIE sources) → caller shows "Not provided".
 */
export const getBundleSourceDisplayName = (
  bundle: ObservationBundle
): string | undefined => {
  const performerDisplay = (bundle?.entry ?? [])
    .filter((entry) => entry.resource?.resourceType === "DiagnosticReport")
    .flatMap((entry) => entry.resource?.performer ?? [])
    .map((performer) => performer.display)
    .find((display): display is string => Boolean(display && display.trim()));

  if (performerDisplay) {
    return performerDisplay;
  }

  return labDisplayNameFor([
    ...getBundleObservationSources(bundle),
    ...getBundleLabProviders(bundle)
  ]);
};

export const getBundleLabProviders = (bundle: ObservationBundle): string[] =>
  getDiagnosticReportPerformerIdentifiers(bundle, PROVIDER_IDENTIFIER_SYSTEM);

export const getBundleObservationSources = (
  bundle: ObservationBundle
): string[] =>
  getDiagnosticReportPerformerIdentifiers(bundle, SOURCE_IDENTIFIER_SYSTEM);

/**
 * Extracts the `source` query param from every bundle.link[].url. This is the
 * backend's aggregation source (e.g. `source=CARE_TEAM_UPLOAD` on a Quest-branded
 * PDF the care team uploaded), which can differ from the DiagnosticReport
 * performer's observation-source (the data's origin, e.g. `questdiagnostics.com`).
 * Both are considered when classifying.
 */
export const getBundleLinkSourceParams = (
  bundle: ObservationBundle
): string[] =>
  (bundle.link ?? [])
    .map((link) => link.url)
    .filter((url): url is string => Boolean(url))
    .map((url) => {
      const queryStart = url.indexOf("?");
      if (queryStart === -1) {
        return null;
      }
      return new URLSearchParams(url.slice(queryStart + 1)).get("source");
    })
    .filter((value): value is string => Boolean(value));

/**
 * True when the bundle's source is specifically a PATIENT upload (the patient
 * uploaded their own file), as opposed to a CARE_TEAM_UPLOAD. Both resolve to
 * the SELF_REPORTED category, but only patient uploads are truly "your files",
 * so the list-row subtitle distinguishes them (see resolveObservationBundleSubtitle).
 */
export const isPatientUploadBundle = (bundle: ObservationBundle): boolean => {
  const sources = [
    ...getBundleObservationSources(bundle),
    ...getBundleLinkSourceParams(bundle)
  ];
  return sources.some((source) =>
    matchesAny(source, [ObservationSource.PATIENT_UPLOAD])
  );
};

const categoryForValues = (
  values: string[]
): ObservationBundleCategory | null => {
  for (const value of values) {
    const match = PROVIDER_CATEGORY_MAP.find((entry) =>
      matchesAny(value, entry.values)
    );
    if (match) {
      return match.category;
    }
  }
  return null;
};

const isLabOrderBundle = (bundle: ObservationBundle): boolean =>
  bundle.identifier?.system === LAB_ORDER_IDENTIFIER_SYSTEM &&
  (bundle.identifier?.type?.coding ?? []).some(
    (coding) => coding.code === LAB_ORDER_CODE
  );

// A real order = a contained ServiceRequest with intent="order" AND a non-empty orderDetail.
// Distinguishes "care team uploaded results of a lab we ordered" (real order) from
// "care team uploaded a document with no backing order" (intent="plan" / no orderDetail).
const hasRealServiceRequestOrder = (bundle: ObservationBundle): boolean =>
  (bundle.entry ?? []).some((entry) =>
    (entry.resource?.contained ?? []).some(
      (contained) =>
        contained.resourceType === "ServiceRequest" &&
        contained.intent === "order" &&
        (contained.orderDetail ?? []).some((detail) =>
          Boolean(detail.text?.trim())
        )
    )
  );

const isObservationDateBundle = (bundle: ObservationBundle): boolean =>
  (bundle.identifier?.type?.coding ?? []).some(
    (coding) => coding.code === OBSERVATION_DATE_CODE
  );

/**
 * Resolves a lab bundle to a single category. First matching rule wins, in the
 * priority order defined in plans/FEWEB-2611-part1/plan.md ("Category model").
 * Defensive: any missing/unexpected shape falls through to UNKNOWN, never throws.
 */
export function resolveObservationBundleCategory(
  bundle: ObservationBundle
): ObservationBundleCategory {
  if (!bundle) {
    return ObservationBundleCategory.UNKNOWN;
  }

  const sources = [
    ...getBundleObservationSources(bundle),
    ...getBundleLinkSourceParams(bundle)
  ];
  const providers = getBundleLabProviders(bundle);

  // 1. Signup-questionnaire bundles are always excluded.
  if (
    sources.some((source) =>
      matchesAny(source, [ObservationSource.INTERNAL_QUESTIONNAIRE])
    )
  ) {
    return ObservationBundleCategory.EXCLUDED;
  }

  // 2. HIE-imported bundle.
  if (
    sources.some((source) => matchesAny(source, OBSERVATION_BUNDLE_HIE_SOURCES))
  ) {
    return ObservationBundleCategory.IMPORTED;
  }

  const isUploadSource = sources.some((source) =>
    matchesAny(source, [
      ObservationSource.CARE_TEAM_UPLOAD,
      ObservationSource.PATIENT_UPLOAD
    ])
  );

  // 3. Self-reported uploads win — unless the bundle carries a real ServiceRequest
  // order (intent="order" + orderDetail), which means the care team uploaded the
  // results of a lab that was actually ordered (still a Regular/AT_HOME/etc lab).
  if (isUploadSource && !hasRealServiceRequestOrder(bundle)) {
    return ObservationBundleCategory.SELF_REPORTED;
  }

  // 4. Lab-order bundle: source label outranks provider label (unless the source
  // is an upload marker on a real order — then classify by the lab provider).
  if (isLabOrderBundle(bundle)) {
    if (isUploadSource) {
      return categoryForValues(providers) ?? ObservationBundleCategory.UNKNOWN;
    }
    return (
      categoryForValues(sources) ??
      categoryForValues(providers) ??
      ObservationBundleCategory.UNKNOWN
    );
  }

  // 5. Observation-date bundle.
  if (isObservationDateBundle(bundle)) {
    return categoryForValues(sources) ?? ObservationBundleCategory.UNKNOWN;
  }

  // 6. Fallback.
  return ObservationBundleCategory.UNKNOWN;
}
